import React, { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import AppListReportsTable from './Table/AppListTable'
import axios from 'axios'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'
import { useSearchParams } from "react-router-dom";

const AppListReports = () => {
    const location = useLocation();
    const navigate = useNavigate();
     const payload = location.state
    const queryClient = useQueryClient();
    const { token } = useSelector((state) => state?.auth);

    const [searchParams] = useSearchParams();

    const code = location?.state?.code || searchParams.get("code");

    const [sort, setSort] = useState(null);
    const [tableOptions, setTableOptions] = useState({
        rowPerPages: 1000, 
        activePage: 0,
    });

    const handleRowsPerPage = (e) => {
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e?.target?.value,
            activePage: 0,
        }));
    };

    const handlePageClick = (event) => {
        setTableOptions((prev) => ({ ...prev, activePage: event?.selected }));
    };

    const getAppList = useCallback(async () => {
        let apiQueryParams = {
            per_page: tableOptions.rowPerPages, 
            page: tableOptions.activePage + 1,
            code: code,
            export: 'N',
        };
        if (sort && sort?.columnName && sort?.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort?.order,
                sort: sort?.columnName,
            };
        }
        return fetchIncidentAnalysisData(apiQueryParams);
    }, [tableOptions?.rowPerPages, tableOptions?.activePage, code, sort]);

    const { isLoading, data: AppListReport, isError, error, isFetching } = useQuery(
        ['fetch-App-List-Report', tableOptions, code, sort],
        getAppList,
        {
            enabled: !!code,
            retry: (failureCount, error) => {
                const status = error?.response?.status;
                return failureCount < 1 && status >= 400 && status < 600;
            },
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
        }
    );

    const handleColumnSort = async (column, sortDirection) => {
        setSort({
            columnName: column?.sortField,
            order: sortDirection,
        });
    };

    const handleOnRowClicked = (row) => {
        const otherDeviceCategory =
            row?.categorization?.['1']?.category_name_tx ||
            row?.other_devices_category_name_tx ||
            '';

        if (otherDeviceCategory === 'NewApp') {
            navigate(`/apps/categorize_new_app/${row?.id}`, {
                state: { from: 'AppListReport' },
            });
        } else {
            navigate(`/apps/editapps/${row?.id}`, {
                state: { from: 'AppListReport' },
            });
        }
    };

    const pageCount = Math.ceil(AppListReport?.total / tableOptions?.rowPerPages);

    // Helper function to chunk arrays (for large PDFs)
    const chunkArray = (array, size) => {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    };

    // Excel export
   const downloadExcel = async () => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
      {
        code: location?.state?.code,
        export: 'Y',
        pagination: 'true', // current page only
        page: tableOptions.activePage + 1,
        per_page: tableOptions.rowPerPages,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob', // required for Excel
      }
    );

    const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'app-list-report.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    console.error(err);
    toasters({ type: 'error', message: 'Error exporting Excel' });
  }
};

const pdfColumns = [
  { header: 'ID', field: 'id' },
  { header: 'App Name', field: 'app_name_tx' },
  { header: 'Platform', field: 'app_platform_tx' },
  { header: 'Package Name', field: 'app_package_name' },
  { header: 'First Seen', field: 'app_first_seen' },
  { header: 'Group Name', field: 'app_group_name' },
  { header: 'Reviewer Notes', field: 'reviewer_notes' },
  { header: 'Date Modified', field: 'date_modified' },

  // Other Device
  { header: 'Other Device Type', field: 'other_device_type' },
  { header: 'Other Device Category', field: 'other_device_category' },
  { header: 'Other Device Score Level', field: 'other_device_score' },

  // Phone
  { header: 'Phone Type', field: 'phone_type' },
  { header: 'Phone Category', field: 'phone_category' },
  { header: 'Phone Score Level', field: 'phone_score' },
];


    // PDF export
const downloadPDF = () => {
  const fullData = AppListReport?.data || [];

  if (!fullData.length) {
    toasters({ type: 'warning', message: 'No data to export' });
    return;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4', // 297mm wide
  });

  doc.setFontSize(16);
  doc.text('App List Report', 14, 12);

  const headers = pdfColumns.map(col => col.header);

  const rows = fullData.map(row => [
    row?.id ?? '',
    row?.app_name_tx ?? '',
    row?.app_platform_tx ?? '',
    row?.app_package_name ?? '',
    row?.app_first_seen ?? '',
    row?.app_group_name ?? '',
    row?.reviewer_notes ?? '',
    row?.date_modified ?? '',
    row?.categorization?.['1']?.app_type_tx || row?.other_devices_app_type_tx || '-',
    row?.categorization?.['1']?.category_name_tx || row?.other_devices_category_name_tx || '-',
    row?.categorization?.['1']?.category_level_cd || row?.other_devices_category_level_cd || '-',
    row?.categorization?.['2']?.app_type_tx || row?.phones_app_type_tx || '-',
    row?.categorization?.['2']?.category_name_tx || '-',
    row?.categorization?.['2']?.category_level_cd || row?.phones_category_level_cd || '-',
  ]);

  doc.autoTable({
    startY: 18,
    head: [headers],
    body: rows,
    theme: 'grid',
    styles: {
      fontSize: 6,           // Reduced font size further to prevent vertical stacking
      cellPadding: 1,        // Reduced padding to save horizontal space
      overflow: 'linebreak', // Allows text to wrap normally
      halign: 'left',
    },
    headStyles: {
      fillColor: [0, 81, 242],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 6,
    },
    columnStyles: {
      0: { cellWidth: 12 },  // ID
      1: { cellWidth: 35 },  // App Name
      2: { cellWidth: 15 },  // Platform
      3: { cellWidth: 35 },  // Package Name
      4: { cellWidth: 22 },  // First Seen
      // Let other columns distribute automatically
    },
    margin: { left: 7, right: 7 },
    tableWidth: 'auto',
  });

  doc.save('app-list-report.pdf');
};


    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <AppListReportsTable
                    data={AppListReport?.data}
                   isLoading={isLoading || isFetching}
                    handleRowsPerPage={handleRowsPerPage}
                    handlePageClick={handlePageClick}
                    tableOptions={tableOptions}
                    totaldata={AppListReport?.total}
                    pageCount={pageCount}
                    handleColumnSort={handleColumnSort}
                    downloadExcel={downloadExcel}
                    handleOnRowClicked={handleOnRowClicked}
                    downloadPDF={downloadPDF}
                      reportCode={payload?.code}
                />
            )}
        </>
    );
};

export default AppListReports;